import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Btn from '../../../../../components/Buttons/Btn';
import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import { frontendNotification } from '../../../../../components/Notification';
import { ContactOtherInfo } from '../../../../../types/apiResponse/contact';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  value: ContactOtherInfo;
  onSave: (value: ContactOtherInfo) => void;
};

type OtherInfoField = {
  key: string;
  value: string;
};

const emptyField = (): OtherInfoField => ({
  key: '',
  value: '',
});

const mapValueToFields = (value: ContactOtherInfo): OtherInfoField[] => {
  const entries = Object.entries(value || {});

  if (entries.length === 0) {
    return [emptyField()];
  }

  return entries.map(([key, itemValue]) => ({
    key,
    value: itemValue,
  }));
};

const OtherInfoModal = ({ showModal, closeModal, value, onSave }: Props) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState<OtherInfoField[]>(() => mapValueToFields(value));

  useEffect(() => {
    if (showModal) {
      setFields(mapValueToFields(value));
    }
  }, [showModal, value]);

  const updateField = (index: number, fieldName: keyof OtherInfoField, fieldValue: string) => {
    setFields((previous) => previous.map((item, itemIndex) => {
      if (itemIndex !== index) {
        return item;
      }

      return {
        ...item,
        [fieldName]: fieldValue,
      };
    }));
  };

  const addField = () => {
    setFields((previous) => [...previous, emptyField()]);
  };

  const removeField = (index: number) => {
    setFields((previous) => {
      const updated = previous.filter((_, itemIndex) => itemIndex !== index);
      return updated.length > 0 ? updated : [emptyField()];
    });
  };

  const handleSave = () => {
    const normalizedFields = fields.filter(
      (item) => item.key.trim().length > 0 || item.value.trim().length > 0,
    );

    if (normalizedFields.some((item) => item.key.trim().length === 0)) {
      frontendNotification({
        message: t('fieldKeyRequired'),
        type: 'warning',
      });

      return;
    }

    const lowerCaseKeys = normalizedFields.map((item) => item.key.trim().toLowerCase());
    const hasRepeatedKeys = lowerCaseKeys.some(
      (key, index) => lowerCaseKeys.indexOf(key) !== index,
    );

    if (hasRepeatedKeys) {
      frontendNotification({
        message: t('repeatedKey'),
        type: 'warning',
      });

      return;
    }

    const mappedValue = normalizedFields.reduce((accumulator, item) => ({
      ...accumulator,
      [item.key.trim()]: item.value.trim(),
    }), {} as ContactOtherInfo);

    onSave(mappedValue);
    closeModal();
  };

  return (
    <Modal
      title={t('otherInfo')}
      showModal={showModal}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={() => handleSave()}
      bodyStyle={{ overflowY: 'auto', maxHeight: '70vh' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {fields.map((field, index) => (
          <div
            key={`${field.key}-${index}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto',
              gap: '12px',
              alignItems: 'end',
            }}
          >
            <Input
              label={t('key')}
              value={field.key}
              onChange={(event) => updateField(index, 'key', event.target.value)}
            />

            <Input
              label={t('value')}
              value={field.value}
              onChange={(event) => updateField(index, 'value', event.target.value)}
            />

            <Btn
              text={t('remove')}
              onClick={() => removeField(index)}
              style={{ maxWidth: '120px', marginBottom: '1px' }}
            />
          </div>
        ))}

        <Btn
          text={t('addField')}
          onClick={() => addField()}
          style={{ maxWidth: '180px', marginTop: '8px' }}
        />
      </div>
    </Modal>
  );
};

export default OtherInfoModal;
